/**
 * KBC Quiz Admin Panel
 * Provides admin interface for managing quiz attempts and users
 * 
 * ADMIN ONLY - FOR EVENT MANAGEMENT
 * Access restricted to authorized admins:
 * - paymanshu.sharma@maersk.com
 * - shantanu.rastogi@maersk.com
 * - srikant.yadav1@maersk.com
 * - krisha.joshi@maersk.com
 * - nikhil.nagesh@maersk.com
 */

import React, { useState, useEffect } from 'react';
import KBCUserProfile from './KBCUserProfile';
import {
  adminGetAllAttempts,
  adminGetAttemptDetails,
  adminDeleteUserAttempts,
  adminDeleteAttempt,
  adminUpdateCompletionStatus,
  adminGetAllUsers,
  adminExportData,
  getQuizStats,
  adminGetPrizeConfig,
  adminUpdatePrizeConfig,
  adminResetPrizeConfig,
  QuizAttempt,
  AdminUser,
  QuizStats,
  PrizeTierConfig,
} from '../../services/quizApi';
import { updateAllPrizeTiers } from './config/prizeConfig';

interface AdminPanelProps {
  adminEmail: string;
  onBack: () => void;
}

interface AttemptDetailsModalProps {
  attempt: QuizAttempt | null;
  onClose: () => void;
}

const AttemptDetailsModal: React.FC<AttemptDetailsModalProps> = ({ attempt, onClose }) => {
  if (!attempt) return null;

  const parsedAnswers = attempt.answers_data 
    ? (typeof attempt.answers_data === 'string' 
        ? JSON.parse(attempt.answers_data) 
        : attempt.answers_data)
    : [];

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gray-800 border-b border-gray-700 p-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-white">Attempt Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-2xl"
          >
            ×
          </button>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-gray-400 text-sm">User Email</p>
              <p className="text-white font-medium">{attempt.user_email}</p>
            </div>
            <div>
              <p className="text-gray-400 text-sm">Display Name</p>
              <p className="text-white font-medium">{attempt.display_name}</p>
            </div>
            <div>
              <p className="text-gray-400 text-sm">Attempt ID</p>
              <p className="text-white font-mono text-xs">{attempt.id}</p>
            </div>
            <div>
              <p className="text-gray-400 text-sm">Status</p>
              <p className="text-white font-medium">
                {attempt.is_completed ? '✅ Completed' : '⏳ In Progress'}
              </p>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-gray-700 rounded-lg p-3">
              <p className="text-gray-400 text-xs">Questions</p>
              <p className="text-white font-bold text-lg">
                {attempt.current_question_index}/{attempt.total_questions}
              </p>
            </div>
            <div className="bg-green-900/30 rounded-lg p-3">
              <p className="text-gray-400 text-xs">Correct</p>
              <p className="text-green-400 font-bold text-lg">{attempt.correct_answers}</p>
            </div>
            <div className="bg-red-900/30 rounded-lg p-3">
              <p className="text-gray-400 text-xs">Incorrect</p>
              <p className="text-red-400 font-bold text-lg">{attempt.incorrect_answers}</p>
            </div>
            <div className="bg-blue-900/30 rounded-lg p-3">
              <p className="text-gray-400 text-xs">Score</p>
              <p className="text-blue-400 font-bold text-lg">{attempt.final_score || 0}</p>
            </div>
          </div>

          {/* Timestamps */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-gray-400 text-sm">Started At</p>
              <p className="text-white text-sm">
                {new Date(attempt.started_at).toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-gray-400 text-sm">Completed At</p>
              <p className="text-white text-sm">
                {attempt.completed_at 
                  ? new Date(attempt.completed_at).toLocaleString() 
                  : 'Not completed'}
              </p>
            </div>
            <div>
              <p className="text-gray-400 text-sm">Time Taken</p>
              <p className="text-white text-sm">
                {attempt.time_taken_seconds 
                  ? `${Math.floor(attempt.time_taken_seconds / 60)}m ${attempt.time_taken_seconds % 60}s`
                  : 'N/A'}
              </p>
            </div>
          </div>

          {/* Prize Info */}
          {attempt.prize_won && (
            <div className="bg-yellow-900/20 border border-yellow-700 rounded-lg p-4">
              <p className="text-yellow-400 font-bold mb-2">🏆 Prize Won</p>
              <p className="text-white">{attempt.prize_won}</p>
              <p className="text-gray-400 text-sm mt-1">
                Level {attempt.prize_level} • 
                {attempt.cashed_out && ' Cashed Out'}
                {attempt.walked_out && ' Walked Out'}
              </p>
            </div>
          )}

          {/* Answers Data */}
          {parsedAnswers.length > 0 && (
            <div>
              <h3 className="text-white font-bold mb-3">Answer History</h3>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {parsedAnswers.map((answer: any, index: number) => (
                  <div
                    key={index}
                    className={`p-3 rounded border ${
                      answer.is_correct 
                        ? 'bg-green-900/20 border-green-700' 
                        : 'bg-red-900/20 border-red-700'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-white font-medium">
                          Q{answer.question_index + 1}: {answer.question_id}
                        </p>
                        <p className="text-gray-300 text-sm">
                          Selected: {answer.selected_answer}
                        </p>
                      </div>
                      <span className="text-2xl">
                        {answer.is_correct ? '✅' : '❌'}
                      </span>
                    </div>
                    {answer.time_taken_seconds && (
                      <p className="text-gray-400 text-xs mt-1">
                        Time: {answer.time_taken_seconds}s
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Raw JSON (collapsed by default) */}
          <details className="bg-gray-900 rounded-lg p-4">
            <summary className="text-gray-400 cursor-pointer hover:text-white">
              View Raw JSON Data
            </summary>
            <pre className="text-xs text-gray-300 mt-3 overflow-x-auto">
              {JSON.stringify(attempt, null, 2)}
            </pre>
          </details>
        </div>
      </div>
    </div>
  );
};

const AdminPanel: React.FC<AdminPanelProps> = ({ adminEmail, onBack }) => {
  const [activeTab, setActiveTab] = useState<'attempts' | 'users' | 'stats' | 'prizes'>('attempts');
  const [attempts, setAttempts] = useState<QuizAttempt[]>([]);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [stats, setStats] = useState<QuizStats | null>(null);
  const [prizeTiers, setPrizeTiers] = useState<PrizeTierConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Filters
  const [searchEmail, setSearchEmail] = useState('');
  const [filterCompleted, setFilterCompleted] = useState<boolean | null>(null);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDesc, setSortDesc] = useState(true);
  
  // Modals
  const [selectedAttempt, setSelectedAttempt] = useState<QuizAttempt | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      if (activeTab === 'attempts') {
        console.log('🔍 AdminPanel: Fetching attempts with filters:', {
          searchEmail,
          filterCompleted,
          sortBy,
          sortDesc,
        });
        
        const attemptsData = await adminGetAllAttempts(adminEmail, {
          user_email_filter: searchEmail || undefined,
          is_completed_filter: filterCompleted !== null ? filterCompleted : undefined,
          order_by: sortBy,
          order_desc: sortDesc,
          limit: 100,
        });
        
        console.log('✅ AdminPanel: Received attempts data:', attemptsData);
        console.log('📊 AdminPanel: Attempts count:', attemptsData?.length || 0);
        
        setAttempts(attemptsData || []);
      } else if (activeTab === 'users') {
        console.log('🔍 AdminPanel: Fetching users');
        const usersData = await adminGetAllUsers(adminEmail);
        console.log('✅ AdminPanel: Received users data:', usersData);
        setUsers(usersData || []);
      } else if (activeTab === 'stats') {
        console.log('🔍 AdminPanel: Fetching stats');
        const statsData = await getQuizStats();
        console.log('✅ AdminPanel: Received stats data:', statsData);
        setStats(statsData);
      } else if (activeTab === 'prizes') {
        console.log('🔍 AdminPanel: Fetching prize config');
        const prizeData = await adminGetPrizeConfig(adminEmail);
        console.log('✅ AdminPanel: Received prize config:', prizeData);
        setPrizeTiers(prizeData || []);
      }
    } catch (err: any) {
      console.error('❌ AdminPanel: Error loading data:', err);
      console.error('❌ AdminPanel: Error details:', {
        message: err.message,
        stack: err.stack,
        response: err.response,
      });
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Auto-load data when component mounts or tab changes
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]); // Re-load when switching tabs

  const handleViewDetails = async (attemptId: string) => {
    try {
      const details = await adminGetAttemptDetails(adminEmail, attemptId);
      setSelectedAttempt(details);
      setShowDetailsModal(true);
    } catch (err: any) {
      alert(`Failed to load details: ${err.message}`);
    }
  };

  const handleDeleteAttempt = async (attemptId: string, userEmail: string) => {
    if (!window.confirm(`Delete attempt for ${userEmail}?\n\nThis action is PERMANENT and cannot be undone!`)) {
      return;
    }

    try {
      await adminDeleteAttempt(adminEmail, attemptId);
      alert('Attempt deleted successfully');
      loadData();
    } catch (err: any) {
      alert(`Failed to delete attempt: ${err.message}`);
    }
  };

  const handleDeleteUserAttempts = async (userEmail: string) => {
    if (!window.confirm(
      `Delete ALL attempts for ${userEmail}?\n\nThis will allow them to play again.\n\nThis action is PERMANENT and cannot be undone!`
    )) {
      return;
    }

    try {
      const result = await adminDeleteUserAttempts(adminEmail, userEmail);
      alert(result.message);
      loadData();
    } catch (err: any) {
      alert(`Failed to delete user attempts: ${err.message}`);
    }
  };

  const handleToggleCompletion = async (attemptId: string, currentStatus: boolean) => {
    if (!window.confirm(
      `Change completion status to ${!currentStatus ? 'COMPLETED' : 'INCOMPLETE'}?\n\nUse with caution!`
    )) {
      return;
    }

    try {
      await adminUpdateCompletionStatus(adminEmail, attemptId, !currentStatus);
      alert('Status updated successfully');
      loadData();
    } catch (err: any) {
      alert(`Failed to update status: ${err.message}`);
    }
  };

  const handleExportData = async () => {
    try {
      const data = await adminExportData(adminEmail);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `kbc-quiz-export-${new Date().toISOString()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(`Failed to export data: ${err.message}`);
    }
  };

  const handleClearLocalStorage = () => {
    if (window.confirm('Clear localStorage cache?\n\nThis will clear the quiz state cache. Useful after deleting user attempts.')) {
      localStorage.removeItem('kbc_quiz_state');
      alert('LocalStorage cleared! Refresh the page to see changes.');
    }
  };

  const handleUpdatePrizeTier = (level: number, field: 'label' | 'enabled', value: string | boolean) => {
    setPrizeTiers(prev => 
      prev.map(tier => 
        tier.level === level 
          ? { ...tier, [field]: value }
          : tier
      )
    );
  };

  const handleSavePrizeConfig = async () => {
    if (!window.confirm('Save prize configuration?\n\nThis will update the prizes shown to users immediately.')) {
      return;
    }

    try {
      setLoading(true);
      const result = await adminUpdatePrizeConfig(adminEmail, prizeTiers);
      
      // Update local prizeConfig.ts state
      updateAllPrizeTiers(prizeTiers);
      
      alert(result.message);
    } catch (err: any) {
      alert(`Failed to save prize config: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPrizeConfig = async () => {
    if (!window.confirm('Reset prize configuration to defaults?\n\nThis will restore the original prize labels and settings.')) {
      return;
    }

    try {
      setLoading(true);
      const result = await adminResetPrizeConfig(adminEmail);
      setPrizeTiers(result.prize_tiers);
      
      // Update local prizeConfig.ts state
      updateAllPrizeTiers(result.prize_tiers);
      
      alert(result.message);
    } catch (err: any) {
      alert(`Failed to reset prize config: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-6 relative">
      {/* User Profile - Top Left (Small) */}
      <KBCUserProfile size="small" position="top-left" />
      
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">🔧 Admin Panel</h1>
            <p className="text-gray-400">Quiz Management Dashboard</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleExportData}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition"
            >
              📥 Export Data
            </button>
            <button
              onClick={handleClearLocalStorage}
              className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition"
              title="Clear localStorage cache (useful after deleting user attempts)"
            >
              🗑️ Clear Cache
            </button>
            <button
              onClick={onBack}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition"
            >
              ← Back
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex gap-2 bg-gray-800 p-2 rounded-lg">
          <button
            onClick={() => setActiveTab('attempts')}
            className={`flex-1 py-2 px-4 rounded-md transition ${
              activeTab === 'attempts'
                ? 'bg-blue-600 text-white'
                : 'text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
          >
            📋 Attempts
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`flex-1 py-2 px-4 rounded-md transition ${
              activeTab === 'users'
                ? 'bg-blue-600 text-white'
                : 'text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
          >
            👥 Users
          </button>
          <button
            onClick={() => setActiveTab('stats')}
            className={`flex-1 py-2 px-4 rounded-md transition ${
              activeTab === 'stats'
                ? 'bg-blue-600 text-white'
                : 'text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
          >
            📊 Stats
          </button>
          <button
            onClick={() => setActiveTab('prizes')}
            className={`flex-1 py-2 px-4 rounded-md transition ${
              activeTab === 'prizes'
                ? 'bg-blue-600 text-white'
                : 'text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
          >
            🏆 Prizes
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto">
        {error && (
          <div className="bg-red-900/20 border border-red-700 rounded-lg p-4 mb-6">
            <p className="text-red-400">❌ {error}</p>
          </div>
        )}

        {loading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            <p className="text-gray-400 mt-4">Loading...</p>
          </div>
        )}

        {!loading && activeTab === 'attempts' && (
          <div>
            {/* Filters */}
            <div className="bg-gray-800 rounded-lg p-4 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <input
                  type="text"
                  placeholder="Search by email..."
                  value={searchEmail}
                  onChange={(e) => setSearchEmail(e.target.value)}
                  className="px-4 py-2 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <select
                  value={filterCompleted === null ? 'all' : filterCompleted ? 'completed' : 'incomplete'}
                  onChange={(e) => 
                    setFilterCompleted(
                      e.target.value === 'all' ? null : e.target.value === 'completed'
                    )
                  }
                  className="px-4 py-2 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Status</option>
                  <option value="completed">Completed</option>
                  <option value="incomplete">Incomplete</option>
                </select>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-4 py-2 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="created_at">Created Date</option>
                  <option value="completed_at">Completed Date</option>
                  <option value="final_score">Score</option>
                  <option value="correct_answers">Correct Answers</option>
                </select>
                <button
                  onClick={() => setSortDesc(!sortDesc)}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition"
                >
                  {sortDesc ? '↓ Descending' : '↑ Ascending'}
                </button>
                <button
                  onClick={() => loadData()}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition font-medium"
                  title="Manually refresh data from database"
                >
                  🔄 Fetch Records
                </button>
              </div>
            </div>

            {/* Attempts Table */}
            <div className="bg-gray-800 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-gray-300 text-sm">User</th>
                      <th className="px-4 py-3 text-left text-gray-300 text-sm">Status</th>
                      <th className="px-4 py-3 text-left text-gray-300 text-sm">Progress</th>
                      <th className="px-4 py-3 text-left text-gray-300 text-sm">Score</th>
                      <th className="px-4 py-3 text-left text-gray-300 text-sm">Prize</th>
                      <th className="px-4 py-3 text-left text-gray-300 text-sm">Created</th>
                      <th className="px-4 py-3 text-left text-gray-300 text-sm">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {attempts.map((attempt) => (
                      <tr key={attempt.id} className="hover:bg-gray-700/50">
                        <td className="px-4 py-3">
                          <p className="text-white font-medium">{attempt.display_name}</p>
                          <p className="text-gray-400 text-sm">{attempt.user_email}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`px-2 py-1 rounded text-xs font-medium ${
                              attempt.is_completed
                                ? 'bg-green-900/30 text-green-400'
                                : 'bg-yellow-900/30 text-yellow-400'
                            }`}
                          >
                            {attempt.is_completed ? '✓ Done' : '⏳ Active'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-white">
                          {attempt.correct_answers}/{attempt.total_questions}
                          <span className="text-gray-400 ml-2">
                            ({Math.round((attempt.correct_answers / attempt.total_questions) * 100)}%)
                          </span>
                        </td>
                        <td className="px-4 py-3 text-white font-bold">
                          {attempt.final_score || 0}
                        </td>
                        <td className="px-4 py-3">
                          {attempt.prize_won ? (
                            <span className="text-yellow-400 text-sm">
                              🏆 {attempt.prize_won}
                            </span>
                          ) : (
                            <span className="text-gray-500 text-sm">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-400 text-sm">
                          {new Date(attempt.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleViewDetails(attempt.id)}
                              className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded transition"
                            >
                              View
                            </button>
                            <button
                              onClick={() => handleToggleCompletion(attempt.id, attempt.is_completed)}
                              className="px-3 py-1 bg-yellow-600 hover:bg-yellow-700 text-white text-sm rounded transition"
                            >
                              Toggle
                            </button>
                            <button
                              onClick={() => handleDeleteAttempt(attempt.id, attempt.user_email)}
                              className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-sm rounded transition"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {attempts.length === 0 && (
                <div className="text-center py-12 text-gray-400">
                  No attempts found
                </div>
              )}
            </div>
          </div>
        )}

        {!loading && activeTab === 'users' && (
          <div className="bg-gray-800 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-gray-300 text-sm">User</th>
                    <th className="px-4 py-3 text-left text-gray-300 text-sm">Total Attempts</th>
                    <th className="px-4 py-3 text-left text-gray-300 text-sm">Completed</th>
                    <th className="px-4 py-3 text-left text-gray-300 text-sm">Last Attempt</th>
                    <th className="px-4 py-3 text-left text-gray-300 text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {users.map((user) => (
                    <tr key={user.user_email} className="hover:bg-gray-700/50">
                      <td className="px-4 py-3">
                        <p className="text-white font-medium">{user.display_name}</p>
                        <p className="text-gray-400 text-sm">{user.user_email}</p>
                      </td>
                      <td className="px-4 py-3 text-white">{user.total_attempts}</td>
                      <td className="px-4 py-3 text-white">{user.completed_attempts}</td>
                      <td className="px-4 py-3 text-gray-400 text-sm">
                        {new Date(user.last_attempt_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleDeleteUserAttempts(user.user_email)}
                          className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-sm rounded transition"
                        >
                          Reset User
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {users.length === 0 && (
              <div className="text-center py-12 text-gray-400">
                No users found
              </div>
            )}
          </div>
        )}

        {!loading && activeTab === 'stats' && stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-gray-800 rounded-lg p-6">
              <p className="text-gray-400 mb-2">Total Attempts</p>
              <p className="text-4xl font-bold text-white">{stats.total_attempts}</p>
            </div>
            <div className="bg-gray-800 rounded-lg p-6">
              <p className="text-gray-400 mb-2">Completed</p>
              <p className="text-4xl font-bold text-green-400">{stats.completed_attempts}</p>
            </div>
            <div className="bg-gray-800 rounded-lg p-6">
              <p className="text-gray-400 mb-2">Active</p>
              <p className="text-4xl font-bold text-yellow-400">{stats.active_attempts}</p>
            </div>
            <div className="bg-gray-800 rounded-lg p-6">
              <p className="text-gray-400 mb-2">Average Score</p>
              <p className="text-4xl font-bold text-blue-400">{stats.average_score.toFixed(1)}</p>
            </div>
            <div className="bg-gray-800 rounded-lg p-6">
              <p className="text-gray-400 mb-2">Cash Outs</p>
              <p className="text-4xl font-bold text-purple-400">{stats.cash_outs}</p>
            </div>
            <div className="bg-gray-800 rounded-lg p-6">
              <p className="text-gray-400 mb-2">Walk Outs</p>
              <p className="text-4xl font-bold text-red-400">{stats.walk_outs}</p>
            </div>
          </div>
        )}

        {!loading && activeTab === 'prizes' && (
          <div>
            {/* Info Banner */}
            <div className="bg-blue-900/20 border border-blue-700 rounded-lg p-4 mb-6">
              <h3 className="text-blue-400 font-bold mb-2">🏆 Prize Configuration</h3>
              <p className="text-gray-300 text-sm mb-2">
                Modify prize labels and availability in real-time during the event.
              </p>
              <ul className="text-gray-400 text-xs space-y-1 list-disc list-inside">
                <li>Changes are saved to backend and applied immediately</li>
                <li>Users will see updated prize info without page refresh</li>
                <li>Disabled prizes cannot be claimed via "Cash Out"</li>
                <li>Use during event to manage prize availability</li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 mb-6">
              <button
                onClick={handleSavePrizeConfig}
                disabled={loading}
                className="px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white rounded-lg transition font-bold"
              >
                💾 Save Changes
              </button>
              <button
                onClick={handleResetPrizeConfig}
                disabled={loading}
                className="px-6 py-3 bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-600 text-white rounded-lg transition font-bold"
              >
                🔄 Reset to Defaults
              </button>
              <button
                onClick={() => loadData()}
                disabled={loading}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded-lg transition"
              >
                🔄 Reload Config
              </button>
            </div>

            {/* Prize Tiers Editor */}
            <div className="space-y-4">
              {prizeTiers.map((tier) => (
                <div
                  key={tier.level}
                  className="bg-gray-800 rounded-lg p-6 border-2 border-gray-700 hover:border-gray-600 transition"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">
                        {tier.level === 5 ? '👑' : tier.level === 4 ? '💎' : tier.level === 3 ? '🎁' : tier.level === 2 ? '📦' : '🎉'}
                      </span>
                      <div>
                        <h3 className="text-white font-bold text-lg">
                          Tier {tier.level} Prize
                        </h3>
                        <p className="text-gray-400 text-sm">
                          Questions {(tier.level - 1) * 4 + 1}-{tier.level * 4}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={tier.enabled}
                          onChange={(e) => handleUpdatePrizeTier(tier.level, 'enabled', e.target.checked)}
                          className="w-5 h-5 rounded"
                        />
                        <span className={`font-medium ${tier.enabled ? 'text-green-400' : 'text-red-400'}`}>
                          {tier.enabled ? '✓ Enabled' : '✗ Disabled'}
                        </span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-400 text-sm mb-2">
                      Prize Label (shown to users)
                    </label>
                    <input
                      type="text"
                      value={tier.label}
                      onChange={(e) => handleUpdatePrizeTier(tier.level, 'label', e.target.value)}
                      className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium"
                      placeholder="Enter prize description..."
                    />
                  </div>

                  {!tier.enabled && (
                    <div className="mt-3 bg-red-900/20 border border-red-700 rounded p-2">
                      <p className="text-red-400 text-sm">
                        ⚠️ This prize is disabled. Users cannot cash out at this tier.
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Warning */}
            <div className="mt-6 bg-yellow-900/20 border border-yellow-700 rounded-lg p-4">
              <p className="text-yellow-400 text-sm font-medium">
                ⚠️ <strong>Important:</strong> Save changes before leaving this tab. 
                Unsaved changes will be lost. Changes take effect immediately for all users.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Details Modal */}
      {showDetailsModal && (
        <AttemptDetailsModal
          attempt={selectedAttempt}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedAttempt(null);
          }}
        />
      )}
    </div>
  );
};

export default AdminPanel;
